import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CreditCard, Check, Sparkles, Apple, Wallet } from "lucide-react";

const METHODS = [
  { label: "Kort (Visa, Mastercard)", icon: CreditCard },
  { label: "Apple Pay", icon: Apple },
  { label: "Google Pay", icon: Wallet },
  { label: "Klarna", icon: Wallet },
  { label: "Link", icon: Wallet },
];

export function SwishPricing({ audience = "candidate" }) {
  const [pricing, setPricing] = useState(null);
  const [payingId, setPayingId] = useState(null);

  const load = () => api.get("/pricing").then((r) => setPricing(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const plans = pricing ? (audience === "employer" ? pricing.employer_plans : pricing.candidate_plans) : [];

  const startStripe = async (plan) => {
    setPayingId(plan.id);
    try {
      const { data } = await api.post("/payments/stripe/checkout", { plan_id: plan.id, origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else throw new Error("no url");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Kunde inte starta betalningen");
      setPayingId(null);
    }
  };

  const startSubscribe = async () => {
    setPayingId("sub");
    try {
      const { data } = await api.post("/payments/stripe/subscribe", { origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else throw new Error("no url");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Kunde inte starta abonnemanget");
      setPayingId(null);
    }
  };

  const openPortal = async () => {
    setPayingId("portal");
    try {
      const { data } = await api.post("/payments/stripe/portal", { origin_url: window.location.origin });
      if (data.portal_url) window.location.href = data.portal_url;
      else throw new Error("no url");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Kunde inte öppna kundportalen");
      setPayingId(null);
    }
  };

  if (!pricing) return null;

  return (
    <Card className="rounded-2xl border-border p-5 md:p-6" data-testid="swish-pricing">
      <div className="flex items-center gap-2 font-head font-semibold text-lg mb-1"><CreditCard className="h-5 w-5 text-primary" /> {audience === "employer" ? "Betala per sökning" : "Lås upp fler CV:n"}</div>
      <p className="text-sm text-muted-foreground mb-4">
        {audience === "employer"
          ? "Varje kandidatsökning betalas enkelt med kort eller Klarna."
          : "Betala säkert med kort, Apple Pay, Google Pay, Klarna eller Link – ditt konto aktiveras direkt."}
        {audience === "employer" && typeof pricing.your_search_credits === "number" && !pricing.is_admin && (
          <span className="ml-1 font-medium text-foreground">Du har {pricing.your_search_credits} sökning{pricing.your_search_credits === 1 ? "" : "ar"} kvar.</span>
        )}
      </p>

      <div className={`grid gap-3 ${plans.length > 1 ? "sm:grid-cols-3" : ""} mb-4`}>
        {plans.map((p) => (
          <div key={p.id} className="rounded-xl border border-border p-4 flex flex-col" data-testid={`plan-${p.id}`}>
            <div className="font-head font-semibold">{p.name}</div>
            <div className="text-2xl font-bold text-primary mt-1">{p.price} kr</div>
            {p.description && <div className="text-xs text-muted-foreground mt-1 flex-1">{p.description}</div>}
            <Button className="rounded-full mt-3 w-full" size="sm" onClick={() => startStripe(p)} disabled={payingId === p.id} data-testid={`pay-card-${p.id}`}>
              {payingId === p.id ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <CreditCard className="h-4 w-4 mr-1.5" />} Betala {p.price} kr
            </Button>
          </div>
        ))}
      </div>

      {audience === "candidate" && (
        <div className="rounded-xl border border-primary/25 bg-gradient-to-br from-primary/[0.07] to-transparent p-4 mb-4" data-testid="subscription-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-head font-semibold flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-primary" /> Obegränsat abonnemang</div>
              <p className="text-sm text-muted-foreground mt-0.5">Skapa hur många CV du vill – {pricing.subscription_price || 149} kr/mån. Säg upp när du vill.</p>
            </div>
            {pricing.is_subscribed ? (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-primary inline-flex items-center gap-1" data-testid="subscription-active"><Check className="h-3.5 w-3.5" /> Aktivt</span>
                <Button variant="outline" size="sm" className="rounded-full" onClick={openPortal} disabled={payingId === "portal"} data-testid="manage-subscription-btn">
                  {payingId === "portal" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Hantera abonnemang
                </Button>
              </div>
            ) : pricing.plan === "unlimited" ? (
              <span className="text-xs text-muted-foreground">Du har redan obegränsad åtkomst.</span>
            ) : (
              <Button size="sm" className="rounded-full" onClick={startSubscribe} disabled={payingId === "sub"} data-testid="subscribe-btn">
                {payingId === "sub" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />} Starta abonnemang
              </Button>
            )}
          </div>
        </div>
      )}

      <div className="rounded-xl bg-primary/[0.05] border border-primary/15 p-4" data-testid="payment-methods-info">
        <div className="text-sm font-medium mb-2">Betalsätt som erbjuds svenska kunder</div>
        <div className="flex flex-wrap gap-2">
          {METHODS.map((m) => (
            <span key={m.label} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1 text-xs">
              <m.icon className="h-3.5 w-3.5 text-primary" /> {m.label}
            </span>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-3">Betalningar hanteras säkert av Stripe. Priser inkl. moms. Du får kvitto direkt och hittar alla köp under Betalningshistorik.</p>
      </div>
    </Card>
  );
}
